/**
 * Der Verlauf über die Monate — die Karte über der Liste der Tage im
 * Essenz-Spiegel. (29.09.2026, Ticket 2789)
 *
 * Martin: *„Stimmung und die weiteren Tagesparameter als Diagramm über
 * Wochen und Monate.“* Was gemessen ist und wie gerechnet wird, steht in
 * shared/tagesverlauf.ts; hier wird nur gezeichnet.
 *
 * Form: kleine Vielfache — je Kenngröße eine eigene Kurve auf EINER
 * gemeinsamen Zeitachse, jede mit ihrer eigenen Skala (1–5 neben 0–100
 * auf einer Achse wäre eine erfundene Nähe). Punkte je Tag, die Linie ist
 * das Wochenmittel, die Zahl unter jedem Monat sein Mittel.
 *
 * ⚠️ Die Farbe trägt keine Bedeutung allein: Jede Kurve steht unter ihrem
 *    Namen. Die Werte eines Tages stehen als Text in der Anzeige darüber,
 *    die Monatsmittel zusätzlich als Tabelle.
 *
 * ⚠️ Keine Stile im Code — Farben, Stärken und Größen stehen in style.css
 *    (`.tv-*`); hier entstehen nur Klassen und die Geometrie.
 */
import { tagesTitel } from "../../shared/spiegel-zeitleiste.js";
import { gesteuert } from "./ruckel-schalter.js";
import {
  ZEITRAEUME,
  baueVerlauf,
  mittelText,
  naechsterGemessener,
  tageswertText,
  zeitraumWahl,
  type Tageswert,
  type Verlauf,
} from "../../shared/tagesverlauf.js";

const NS = "http://www.w3.org/2000/svg";

/** Rand links/rechts, damit Punkte am Rand nicht angeschnitten werden. */
const RAND = 6;
/** Innenabstand oben/unten im Kurvenfeld. */
const LUFT = 6;
/** Höhe des Bandes mit den Monatsmitteln unter jeder Kurve. */
const BAND = 16;
/** Ungefähre Breite eines Zeichens der 11-px-Beschriftung. */
const ZEICHEN_PX = 6.4;

export interface TageswerteAntwort {
  heute: string;
  tage: Tageswert[];
}

function zahlOderNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Die Tageswerte holen — null, wenn es nicht geht. */
export async function ladeTageswerte(signal?: AbortSignal): Promise<TageswerteAntwort | null> {
  try {
    const r = await fetch("/api/essence-mirror/tageswerte", {
      credentials: "same-origin",
      ...(signal ? { signal } : {}),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { ok?: boolean; bis?: unknown; tage?: unknown };
    if (!j.ok || typeof j.bis !== "string" || !Array.isArray(j.tage)) return null;
    const tage: Tageswert[] = [];
    for (const t of j.tage as Array<Record<string, unknown>>) {
      if (!t || typeof t.day !== "string") continue;
      tage.push({
        day: t.day,
        verfassung: zahlOderNull(t.verfassung),
        geist: zahlOderNull(t.geist),
        seele: zahlOderNull(t.seele),
        erholung: zahlOderNull(t.erholung),
      });
    }
    return { heute: j.bis, tage };
  } catch {
    return null;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  klasse: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  klasse: string,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, tag);
  e.setAttribute("class", klasse);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

/** Die Geometrie eines Bildes: Tag → x, Wert → y. */
function geometrie(v: Verlauf, breite: number, hoehe: number) {
  const innen = Math.max(1, breite - 2 * RAND);
  const schritt = v.n > 1 ? innen / (v.n - 1) : 0;
  const x = (i: number): number => (v.n > 1 ? RAND + i * schritt : breite / 2);
  const y = (wert: number, min: number, max: number): number =>
    LUFT + (1 - (wert - min) / (max - min || 1)) * (hoehe - 2 * LUFT);
  // Breite einer Monatsspalte in Pixeln (für die Beschriftung).
  const spaltenBreite = (i0: number, i1: number): number =>
    v.n > 1 ? (i1 - i0 + 1) * schritt : breite;
  const tagesPunkt = Math.max(1.4, Math.min(2.6, schritt * 0.42 || 2.6));
  return { x, y, schritt, spaltenBreite, tagesPunkt };
}

/** Passt ein Text in eine Spalte? Sonst die kürzere Form oder nichts. */
function passend(breite: number, formen: string[]): string | null {
  for (const f of formen) if (f.length * ZEICHEN_PX + 6 <= breite) return f;
  return null;
}

const f1 = (n: number): string => n.toFixed(1);

/**
 * Die Karte. Sie lädt ihre Zahlen selbst und bleibt unsichtbar, solange —
 * oder wenn überhaupt — keine gemessene Zahl da ist.
 */
export function baueTagesverlauf(o?: {
  signal?: AbortSignal;
  laden?: (signal?: AbortSignal) => Promise<TageswerteAntwort | null>;
}): HTMLElement {
  const wurzel = el("section", "tv-wurzel");
  wurzel.hidden = true;
  void (o?.laden ?? ladeTageswerte)(o?.signal).then((d) => {
    if (d) zeigeTagesverlauf(wurzel, d);
  });
  return wurzel;
}

let laufendeNummer = 0;

/** Zeichnet die Karte in `wurzel` — synchron, damit die Prüfung sie sieht. */
export function zeigeTagesverlauf(wurzel: HTMLElement, daten: TageswerteAntwort): void {
  const erster = [...daten.tage].map((t) => t.day).sort()[0] ?? null;
  const wahl = zeitraumWahl(erster, daten.heute);
  let monate = wahl.vorgabe;
  let verlauf = baueVerlauf(daten.tage, daten.heute, monate);
  wurzel.replaceChildren();
  if (!verlauf) {
    wurzel.hidden = true;
    return;
  }
  wurzel.hidden = false;
  const nr = ++laufendeNummer;
  wurzel.setAttribute("aria-labelledby", `tv-titel-${nr}`);

  // ── Kopf: Titel, Lesehilfe, Zeitraum ──────────────────────────────────
  const kopf = el("div", "tv-kopf");
  const kopfText = el("div", "tv-kopf-text");
  const titel = el("h3", "zl-abschnitt-titel tv-titel", "Über die Monate");
  titel.id = `tv-titel-${nr}`;
  kopfText.append(
    titel,
    el(
      "p",
      "tv-unter",
      "Punkt = ein Tag · Linie = Wochenmittel · Ø = Monatsmittel. Tippe in die Kurven, um einen Tag zu wählen.",
    ),
  );
  kopf.appendChild(kopfText);
  const zeitraum = el("div", "tv-zeitraum");
  zeitraum.setAttribute("role", "group");
  zeitraum.setAttribute("aria-label", "Zeitraum");
  const zeitKnoepfe: HTMLButtonElement[] = [];
  if (wahl.angebote.length > 1) {
    for (const z of ZEITRAEUME.filter((r) => wahl.angebote.includes(r.monate))) {
      const b = el("button", "tv-zeitraum-knopf", z.name);
      b.type = "button";
      b.dataset.monate = String(z.monate);
      b.addEventListener("click", () => {
        if (monate === z.monate) return;
        monate = z.monate;
        verlauf = baueVerlauf(daten.tage, daten.heute, monate);
        aktiv = neuester();
        allesNeu();
      });
      zeitKnoepfe.push(b);
      zeitraum.appendChild(b);
    }
    kopf.appendChild(zeitraum);
  }

  // ── Anzeige eines Tages ──────────────────────────────────────────────
  const anzeige = el("p", "tv-anzeige");
  anzeige.id = `tv-anzeige-${nr}`;
  anzeige.setAttribute("aria-live", "polite");

  // ── Die Fläche: Monatsleiste und je Kenngröße eine Kurve ─────────────
  const flaeche = el("div", "tv-flaeche");
  flaeche.tabIndex = 0;
  flaeche.setAttribute("role", "group");
  flaeche.setAttribute(
    "aria-label",
    "Verlauf je Tag. Mit den Pfeiltasten einen Tag wählen, Pos1 und Ende für den ersten und letzten.",
  );
  flaeche.setAttribute("aria-describedby", anzeige.id);

  const tabelle = el("details", "tv-tabelle");

  wurzel.append(kopf, anzeige, flaeche, tabelle);

  /*
   * Der gewählte Tag. Ohne Wahl der NEUESTE gemessene — die Anzeige zeigt
   * sofort etwas, statt eine leere Zeile mit einer Gebrauchsanweisung.
   */
  const neuester = (): number | null => verlauf?.gemessen[verlauf.gemessen.length - 1] ?? null;
  let aktiv: number | null = neuester();
  let breite = 0;

  const setzeAnzeige = (): void => {
    anzeige.replaceChildren();
    const v = verlauf;
    if (!v) return;
    if (aktiv == null) return;
    const i = aktiv;
    const day = v.reihen.flatMap((r) => r.punkte).find((p) => p.i === i)?.day;
    if (!day) return;
    anzeige.appendChild(el("strong", "tv-anzeige-tag", tagesTitel(day)));
    for (const r of v.reihen) {
      const p = r.punkte.find((q) => q.i === i);
      if (!p) continue;
      const w = el("span", "tv-anzeige-wert");
      w.dataset.key = r.def.key;
      const schluessel = el("span", "tv-schluessel");
      schluessel.setAttribute("aria-hidden", "true");
      w.append(schluessel, `${r.def.name} `, el("b", "", tageswertText(r.def, p.wert)));
      anzeige.appendChild(w);
    }
  };

  const zeichne = (): void => {
    const v = verlauf;
    if (!v) return;
    flaeche.replaceChildren();
    const b = breite || 600;
    const kurvenHoehe = b < 480 ? 52 : 64;
    const g = geometrie(v, b, kurvenHoehe);

    // Monatsleiste — einmal, über allen Kurven.
    const leiste = svgEl("svg", "tv-monate", { width: b, height: 16, viewBox: `0 0 ${b} 16` });
    leiste.setAttribute("aria-hidden", "true");
    for (const s of v.monate) {
      const w = g.spaltenBreite(s.i0, s.i1);
      const text = passend(w, [s.name, s.kurz, s.name.slice(0, 3)]);
      if (!text) continue;
      const t = svgEl("text", "tv-monat", { x: f1((g.x(s.i0) + g.x(s.i1)) / 2), y: 12, "text-anchor": "middle" });
      t.textContent = text;
      leiste.appendChild(t);
    }
    flaeche.appendChild(leiste);

    for (const r of v.reihen) {
      const reihe = el("div", "tv-reihe");
      reihe.dataset.key = r.def.key;
      const rk = el("div", "tv-reihe-kopf");
      const schluessel = el("span", "tv-schluessel");
      schluessel.setAttribute("aria-hidden", "true");
      rk.append(schluessel, el("span", "tv-name", r.def.name), el("span", "tv-skala", r.def.skala));
      reihe.appendChild(rk);

      const h = kurvenHoehe + BAND;
      const svg = svgEl("svg", "tv-kurve", { width: b, height: h, viewBox: `0 0 ${b} ${h}` });
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
      const y = (w: number): number => g.y(w, r.def.min, r.def.max);

      // Raster: Grundlinie, Mitte, Monatsgrenzen — Haarlinien.
      svg.appendChild(svgEl("line", "tv-raster", { x1: 0, x2: b, y1: kurvenHoehe - 0.5, y2: kurvenHoehe - 0.5 }));
      const mitte = f1(y((r.def.min + r.def.max) / 2));
      svg.appendChild(svgEl("line", "tv-raster tv-raster--mitte", { x1: 0, x2: b, y1: mitte, y2: mitte }));
      for (const s of v.monate.slice(1)) {
        const xs = f1(g.x(s.i0) - g.schritt / 2);
        svg.appendChild(svgEl("line", "tv-raster", { x1: xs, x2: xs, y1: 0, y2: kurvenHoehe }));
      }

      // Punkte je Tag — leise.
      for (const p of r.punkte) {
        const c = svgEl("circle", "tv-tag", { cx: f1(g.x(p.i)), cy: f1(y(p.wert)), r: f1(g.tagesPunkt) });
        c.dataset.day = p.day;
        svg.appendChild(c);
      }

      // Wochenmittel — die Linie; eine Woche ohne Wert trennt.
      for (const stueck of r.wochen) {
        const einzeln = stueck.length === 1 ? stueck[0] : undefined;
        if (einzeln) {
          const w = einzeln;
          svg.appendChild(svgEl("circle", "tv-woche-punkt", { cx: f1(g.x(w.x)), cy: f1(y(w.wert)), r: 2.75 }));
          continue;
        }
        const d = stueck.map((w, k) => `${k ? "L" : "M"}${f1(g.x(w.x))} ${f1(y(w.wert))}`).join("");
        svg.appendChild(svgEl("path", "tv-woche", { d }));
      }

      // Monatsmittel im Band darunter.
      r.monate.forEach((m, k) => {
        const s = v.monate[k];
        if (!s || m.mittel == null) return;
        const zahl = mittelText(r.def, m.mittel);
        const text = passend(g.spaltenBreite(s.i0, s.i1), [`Ø ${zahl}`, zahl]);
        if (!text) return;
        const t = svgEl("text", "tv-mittel", {
          x: f1((g.x(s.i0) + g.x(s.i1)) / 2),
          y: kurvenHoehe + 12,
          "text-anchor": "middle",
        });
        t.textContent = text;
        svg.appendChild(t);
      });

      // Der gewählte Tag.
      if (aktiv != null) {
        const xa = f1(g.x(aktiv));
        svg.appendChild(svgEl("line", "tv-aktiv-linie", { x1: xa, x2: xa, y1: 0, y2: kurvenHoehe }));
        const p = r.punkte.find((q) => q.i === aktiv);
        if (p) svg.appendChild(svgEl("circle", "tv-aktiv-punkt", { cx: xa, cy: f1(y(p.wert)), r: 4 }));
      }

      reihe.appendChild(svg);
      flaeche.appendChild(reihe);
    }
  };

  const zeichneTabelle = (): void => {
    const v = verlauf;
    tabelle.replaceChildren();
    if (!v) return;
    tabelle.appendChild(el("summary", "tv-tabelle-knopf", "Monatsmittel als Tabelle"));
    const t = el("table", "tv-tabelle-tafel");
    t.appendChild(
      el("caption", "tv-tabelle-titel", "Mittel der gemessenen Tage je Monat, darunter die Zahl der Tage mit Wert."),
    );
    const kopfZeile = el("tr", "");
    kopfZeile.appendChild(el("th", "", "Monat"));
    for (const r of v.reihen) {
      const th = el("th", "", r.def.name);
      th.scope = "col";
      kopfZeile.appendChild(th);
    }
    const thead = el("thead", "");
    thead.appendChild(kopfZeile);
    const tbody = el("tbody", "");
    const heuteJahr = Number(daten.heute.slice(0, 4));
    v.monate.forEach((s, k) => {
      const tr = el("tr", "");
      const th = el("th", "", s.jahr !== heuteJahr ? `${s.name} ${s.jahr}` : s.name);
      th.scope = "row";
      tr.appendChild(th);
      for (const r of v.reihen) {
        const m = r.monate[k];
        const td = el("td", "");
        if (m?.mittel != null) {
          td.append(
            el("span", "tv-tabelle-zahl", mittelText(r.def, m.mittel)),
            el("span", "tv-tabelle-n", `${m.n} ${m.n === 1 ? "Tag" : "Tage"}`),
          );
        } else {
          td.appendChild(el("span", "zl-sr", "keine Messung"));
        }
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    });
    t.append(thead, tbody);
    tabelle.appendChild(t);
  };

  const allesNeu = (): void => {
    for (const k of zeitKnoepfe) k.setAttribute("aria-pressed", String(Number(k.dataset.monate) === monate));
    zeichne();
    setzeAnzeige();
    zeichneTabelle();
  };

  const waehle = (i: number | null): void => {
    if (i === aktiv) return;
    aktiv = i;
    zeichne();
    setzeAnzeige();
  };

  // ── Tippen, Zeigen, Tasten ───────────────────────────────────────────
  const tagAn = (clientX: number): number | null => {
    const v = verlauf;
    const ziel = flaeche.querySelector("svg");
    if (!v || !ziel) return null;
    const box = ziel.getBoundingClientRect();
    const g = geometrie(v, box.width || breite || 600, 52);
    const roh = g.schritt > 0 ? Math.round((clientX - box.left - RAND) / g.schritt) : 0;
    return naechsterGemessener(v.gemessen, Math.max(0, Math.min(v.n - 1, roh)));
  };
  flaeche.addEventListener("pointerdown", (e: PointerEvent) => waehle(tagAn(e.clientX)));
  flaeche.addEventListener("pointermove", (e: PointerEvent) => {
    if (e.pointerType === "mouse" || e.buttons > 0) waehle(tagAn(e.clientX));
  });
  flaeche.addEventListener("pointerleave", (e: PointerEvent) => {
    if (e.pointerType === "mouse") waehle(neuester());
  });
  flaeche.addEventListener("keydown", (e: KeyboardEvent) => {
    const v = verlauf;
    if (!v?.gemessen.length) return;
    const liste = v.gemessen;
    const pos = aktiv == null ? -1 : liste.indexOf(aktiv);
    let neu: number | null | undefined;
    if (e.key === "ArrowLeft") neu = pos < 0 ? liste[liste.length - 1] : liste[Math.max(0, pos - 1)];
    else if (e.key === "ArrowRight") neu = pos < 0 ? liste[liste.length - 1] : liste[Math.min(liste.length - 1, pos + 1)];
    else if (e.key === "Home") neu = liste[0];
    else if (e.key === "End") neu = liste[liste.length - 1];
    // Escape bleibt beim Blatt: es schließt den Spiegel wie überall.
    else return;
    e.preventDefault();
    waehle(neu ?? null);
  });

  // Breite messen und bei Änderung neu zeichnen (Drehen, Seitenleiste).
  breite = Math.round(flaeche.clientWidth);
  allesNeu();
  if (typeof ResizeObserver !== "undefined") {
    // Aufräumen, sobald die Karte aus dem Blatt genommen ist (Liste → Tag).
    let warDa = flaeche.isConnected;
    const ro = new ResizeObserver(
      gesteuert(
        "wachen",
        () => {
          if (!flaeche.isConnected) {
            if (warDa) ro.disconnect();
            return;
          }
          warDa = true;
          const neu = Math.round(flaeche.clientWidth);
          if (neu > 0 && neu !== breite) {
            breite = neu;
            zeichne();
          }
        },
        true,
      ),
    );
    ro.observe(flaeche);
  }
}

